import {debounce, hideElement, showElement} from "../utils";

export default class Former {
    blockElementRoot: HTMLDivElement | null = null;

    blockElementForm: HTMLFormElement | null;

    blockHideableElementSelector: string | null = null;

    blockModifiers = {
        expanded: '--expanded',
        filtered: '--filtered',
    }

    elementDataSearchableTypes: { text?: null | string, type?: null | string } = {
        text: null,
        type: null,
    }

    constructor(elementRoot: HTMLDivElement) {
        this.blockElementRoot = elementRoot;

        if (this.blockElementRoot) {
            this.blockElementForm = this.blockElementRoot.querySelector('form') || null;
        }

        /**
         * Should we handle if there is no form?
         */
        this.initListeners()
    }

    initListeners() {
        if (this.blockElementForm) {
            /**
             *
             */
            this.blockElementForm.addEventListener('submit', this.processForm.bind(this));

            /**
             * Go through all selects in the form and add a change listener that submits the form
             */
            [...this.blockElementForm.querySelectorAll('select, input[type=date], input[type=month]')].map(
                (element: HTMLSelectElement) => element.addEventListener('change', () => element.form.requestSubmit())
            );

            /**
             * Go through all text inputs in the form and add a blur listener (when focus leaves the search) that submits the form
             */
            [...this.blockElementForm.querySelectorAll('input[type="text"]')].map(
                (element: HTMLInputElement) => {

                    /**
                     * debounce keyup (otherwise it runs for each keyup
                     */
                    const debounceFn = debounce(() => element.form.requestSubmit(), 300);

                    /**
                     * Add events for multiple events that uses the debounce functionality
                     */
                    ['blur', 'keyup'].map((eventType) => element.addEventListener(eventType, () => debounceFn()))
                }
            )
        }
    }

    processForm(event: SubmitEvent) {
        event.preventDefault(); // don't you send that to the server

        const formData: FormData = new FormData(this.blockElementForm);

        const formDataObject: any = Object.fromEntries(formData) as any;

        return {formDataObject, formData, event};
    }

    expand() {
        if (this.blockElementRoot) {
            this.blockElementRoot.classList.add(this.blockModifiers.expanded);
        }
    }


    toggleItems(selectors: string) {

        if (!this.blockHideableElementSelector) {
            this.blockElementRoot.classList.remove(this.blockModifiers.filtered)
            return; // well there is no selector to use, so why should we continue?
        } else {
            this.blockElementRoot.classList.add(this.blockModifiers.filtered)
        }

        const allHideableElements = this.blockElementRoot.querySelectorAll(this.blockHideableElementSelector);


        /**
         * To debug selectors, please add a console.log(selectors) and check them out
         */

        allHideableElements.forEach((element) => {
            /**
             * If empty selectors show all so there is no elements hidden
             */
            if (!selectors) {
                showElement(element as HTMLElement);
                return;
            }

            /**
             * Check if it matches the selectors, if it does. Show it
             */
            if (element.matches(selectors)) {
                showElement(element as HTMLElement);
            } else {
                hideElement(element as HTMLElement);
            }

        })
    }

    generateSelectors(requiredGroup: { [key: string]: string }, textGroup: { [key: string]: string }) {

        let requiredGroupSelectors = '';
        let textGroupSelectors = '';

        /**
         * Require selectors will get chained like this:
         * [data-type-x-y=z][data-type-x-y=z]
         */

        if (this.elementDataSearchableTypes.type) {
            requiredGroupSelectors = Object.keys(requiredGroup).map((key: string) => {
                return requiredGroup[key] ? `[data-${this.elementDataSearchableTypes.type}-${key}*="${requiredGroup[key]}"]` : null;
            }).filter((item) => item).join('');
        }

        /**
         * Text selectors will get optional chained like this:
         * [data-text-x-y~=z], [data-text-x-y~=z]
         *
         * Every word will get it's own selector
         * If we need substring searching, we can change the selector from ~= to *=
         */

        if (this.elementDataSearchableTypes.text) {
            textGroupSelectors = Object.keys(textGroup).map((key: 'text-search') => {
                const chunkedSearchInput = textGroup[key].split(' ').filter((string: string) => string)

                return chunkedSearchInput.map((searchKeyword: string) => {
                    return `[data-${this.elementDataSearchableTypes.text}*="${searchKeyword.toLowerCase()}" i]`; // "i" in the end is to make it case-insensitive
                })
            }).join(',');
        }

        return `${requiredGroupSelectors}${textGroupSelectors ? `:where(${textGroupSelectors})` : ''}`
    }
}